import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { AppProps } from 'types'
import Cookies from 'universal-cookie'
import theme from '../styles/theme'
import { useQueryClient } from '@tanstack/react-query'

const cookies = new Cookies()

const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null

export const initialContext: AppProps = {
  sdk: false,
  app: typeof window !== 'undefined' ? window.APP : undefined,
  setApp: () => undefined,
  baseUrl: typeof window !== 'undefined' ? window.URL_FRONTEND : undefined,
  clientId: undefined,
  userToken: undefined,
  extension: undefined,
  callback: () => undefined,
  routeTo: undefined,
  applicantData: undefined,
  companyData: undefined,
  jobData: undefined,
  profiles: undefined,
  customize: {
    accentColor: (typeof window !== 'undefined' ? window.APP?.accentColor : undefined) ?? '#3ca',
    primaryFont: theme.primaryFont,
    secondaryFont: theme.secondaryFont,
    taskColor: '#0bd',
    peopleColor: '#9c5',
    contextColor: '#fc4',
    compareColor: '#f60',
    positioningColor: '#4b9faa',
    primaryErrorColor: '#F02849',
    secondaryErrorColor: '#EC5990',
    tourColor: '#357cf0',
    doneColor: '#25C133',
    // From SDK
    disableLogo: false,
    disableReportDescription: false,
    defaultReportPage: 'detailed',
    loginUrl: undefined,
    darkMode: typeof window !== 'undefined' ? window.APP?.darkMode : undefined,
    applicantButtonExit: typeof window !== 'undefined' ? window.APP?.applicantButtonExit ?? undefined : undefined,
    jobButtonExit: typeof window !== 'undefined' ? window.APP?.jobButtonExit ?? undefined : undefined,
    enableJobAddApplicantButton: typeof window !== 'undefined' ? window.APP?.enableJobAddApplicantButton ?? true : true,
  },
  openCookiesConfig: false,
  setOpenCookiesConfig: () => undefined,
  darkMode: cookies.get('theme') ?? 'system',
  setDarkMode: () => undefined,
  locale:
    (searchParams ? searchParams.get('lang') : undefined) ??
    cookies.get('language') ??
    (typeof navigator !== 'undefined' ? navigator.language : undefined) ??
    'es-ES',
  setLocale: () => undefined,
  tour: null,
}

const Context = createContext(initialContext)

type ContextProps = {
  values: AppProps
  children: ReactNode
}

export function ContextProvider({ values, children }: ContextProps) {
  const queryClient = useQueryClient()
  const { sdk, baseUrl, extension, customize, routeTo, callback, applicantData, companyData, jobData, profiles } =
    values
  const clientId = (searchParams ? searchParams.get('clientId') : undefined) ?? values.clientId ?? values.clientId

  const userToken = (searchParams ? searchParams.get('token') : undefined) ?? values.userToken

  // To use in ducks
  if (typeof window !== 'undefined') window.clientId = clientId

  const [app, setApp] = useState(typeof window !== 'undefined' ? window.APP : null)

  const [openCookiesConfig, setOpenCookiesConfig] = useState(false)

  const [locale, setLocale] = useState(values.locale)
  useEffect(() => {
    setLocale(values.locale)
  }, [values.locale])

  useEffect(() => {
    if (typeof window !== 'undefined') window.LOCALE = locale
    queryClient.invalidateQueries()
  }, [locale])

  /**
   * darkMode
   */
  const [darkMode, setDarkMode] = useState(
    values.customize.darkMode != null
      ? values.customize.darkMode
        ? 'dark'
        : 'light'
      : cookies.get('theme') ?? 'system',
  )

  useEffect(() => {
    if (darkMode === 'dark') {
      if (!sdk) cookies.set('theme', 'dark', { path: '/' })

      document.documentElement.classList.add('dark')
    } else if (darkMode === 'light') {
      if (!sdk) cookies.set('theme', 'light', { path: '/' })

      document.documentElement.classList.remove('dark')
    } else {
      if (!sdk) cookies.remove('theme', { path: '/' })

      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }
  }, [darkMode])

  // Tour
  const [tourValue, setTourValue] = useState(false)
  const [otherTeamsTooltip, setOtherTeamsTooltip] = useState(true)
  const [teamsTooltip, setTeamsTooltip] = useState(true)
  const [membersTooltip, setMembersTooltip] = useState(true)
  const [boardTooltip, setBoardTooltip] = useState(true)
  const [boardSortMembersTooltip, setBoardSortMembersTooltip] = useState(true)
  const [boardSortIndicatorsTooltip, setBoardSortIndicatorsTooltip] = useState(true)
  const [profilesTooltip, setProfilesTooltip] = useState(true)
  const [profilesFirstTooltip, setProfilesFirstTooltip] = useState(true)
  const [profilesSecondTooltip, setProfilesSecondTooltip] = useState(true)
  const [profilesCompareTooltip, setProfilesCompareTooltip] = useState(true)
  const [distributionTooltip, setDistributionTooltip] = useState(true)
  const [profileDescription, setProfileDescription] = useState(true)

  // Inputs
  const [inputTeams, setInputTeams] = useState(false)
  const [inputMembers, setInputMembers] = useState(false)
  const [inputAddMembers, setInputAddMembers] = useState(false)
  const [inputProfilesFirst, setProfilesFirst] = useState(false)
  const [inputProfilesSecond, setProfilesSecond] = useState(false)

  const context = useMemo(
    () => ({
      sdk,
      app,
      setApp,
      baseUrl,
      clientId,
      userToken,
      extension,
      // Prioritize app customizations over sdk ones
      customize: {
        ...customize,
        accentColor: app?.accentColor ?? customize.accentColor,
        darkMode: app?.darkMode ?? customize.darkMode,
        applicantButtonExit: app?.applicantButtonExit ?? customize.applicantButtonExit,
        jobButtonExit: app?.jobButtonExit ?? customize.jobButtonExit,
        enableJobAddApplicantButton: app?.enableJobAddApplicantButton ?? customize.enableJobAddApplicantButton,
      },
      routeTo,
      callback,
      applicantData,
      companyData,
      jobData,
      profiles,
      locale,
      setLocale,
      openCookiesConfig,
      setOpenCookiesConfig,
      darkMode,
      setDarkMode,
      tour: {
        user: { value: tourValue, set: setTourValue },
        otherTeams: { value: otherTeamsTooltip, set: setOtherTeamsTooltip },
        teams: { value: teamsTooltip, set: setTeamsTooltip },
        members: { value: membersTooltip, set: setMembersTooltip },
        board: {
          value: boardTooltip,
          set: setBoardTooltip,
          inside: {
            sortMembers: { value: boardSortMembersTooltip, set: setBoardSortMembersTooltip },
            sortIndicators: { value: boardSortIndicatorsTooltip, set: setBoardSortIndicatorsTooltip },
          },
        },
        profiles: {
          value: profilesTooltip,
          set: setProfilesTooltip,
          inside: {
            first: { value: profilesFirstTooltip, set: setProfilesFirstTooltip },
            second: { value: profilesSecondTooltip, set: setProfilesSecondTooltip },
            compare: { value: profilesCompareTooltip, set: setProfilesCompareTooltip },
          },
        },
        distribution: { value: distributionTooltip, set: setDistributionTooltip },
        profileDescription: { value: profileDescription, set: setProfileDescription },
      },
      inputFocused: {
        teams: { value: inputTeams, set: setInputTeams },
        members: { value: inputMembers, set: setInputMembers },
        membersAdd: { value: inputAddMembers, set: setInputAddMembers },
        profiles: {
          first: { value: inputProfilesFirst, set: setProfilesFirst },
          second: { value: inputProfilesSecond, set: setProfilesSecond },
        },
      },
    }),
    [
      sdk,
      app,
      baseUrl,
      clientId,
      userToken,
      extension,
      customize,
      routeTo,
      callback,
      applicantData,
      companyData,
      jobData,
      profiles,
      locale,
      openCookiesConfig,
      darkMode,
      // Tour
      tourValue,
      otherTeamsTooltip,
      teamsTooltip,
      membersTooltip,
      boardTooltip,
      boardSortMembersTooltip,
      boardSortIndicatorsTooltip,
      profilesTooltip,
      profilesFirstTooltip,
      profilesSecondTooltip,
      profilesCompareTooltip,
      distributionTooltip,
      inputTeams,
      inputMembers,
      inputAddMembers,
      inputProfilesFirst,
      inputProfilesSecond,
      profileDescription,
    ],
  )

  return <Context.Provider value={context}>{children}</Context.Provider>
}

export default function useStore() {
  return useContext(Context)
}
